/**
 * hegias design tokens, typed.
 *
 * A mirror of ./tokens.css for consumers that cannot read a CSS custom property:
 * the styled-components theme, the antd ConfigProvider, and any JS that needs a
 * value (canvas, charts, inline SVG). `tokens.spec.ts` fails the build if the two
 * files drift apart, so treat them as one artefact edited in two places.
 *
 * Prefer `var(--token)` in CSS. Reach for this module only from TypeScript.
 *
 * Spec: design-update/DESIGN_SYSTEM.md
 */

export const colors = {
	/* Brand — lime. The lead brand colour, and a FILL colour: it is 2.70:1 on
	   white, so it cannot be seen as a stroke, an icon or a focus ring. Those
	   belong to `secondary`. Carries DARK text. */
	primary: "#94A639",
	primaryTint: "#F5F9E8",
	primaryContrast: "#1F1F1F",

	/* Lime fill ramp — anchored a step higher than purple, because darkening lime
	   lowers contrast against its dark label. Dark text: 6.10 / 6.84 / 4.98 : 1. */
	primaryFill: "#94A639",
	primaryFillHover: "#9FAF4D",
	primaryFillActive: "#859533",

	/* Brand — purple. The CI hue and the interaction colour: at 6.74:1 on white
	   it is the brand colour that reads as a stroke or a glyph. */
	secondary: "#7C4789",
	secondaryTint: "#FBF4FF",
	secondaryContrast: "#FFFFFF",

	/* Purple fill ramp — areas only. White text on each: 7.75 / 5.35 / 8.97 : 1. */
	secondaryFill: "#70407B",
	secondaryFillHover: "#895995",
	secondaryFillActive: "#63396E",

	/* Logo palette — the mark itself. Not UI chrome, not pickable. */
	logoLime: "#94A639",
	logoPurple: "#722973",
	logoNavy: "#2D5671",
	logoGrey: "#6C6C6D",

	/* Neutral ramp — every grey in the interface is one of these six. */
	grey50: "#FBFAFB",
	grey100: "#F2F2F2",
	grey200: "#E4E4E4",
	grey300: "#CECECE",
	grey400: "#9B9B9B",
	grey500: "#6C6C6D",

	/* Surfaces */
	bgApp: "#FFFFFF",
	bgPanel: "#FBFAFB",
	surface: "#FFFFFF",
	fillSubtle: "#F2F2F2",
	bgWorkspace: "#F2F2F2",
	bgViewport: "#ECECEC",

	/* Interaction grounds — neutral, never a brand tint (a purple tint reads pink) */
	hoverGround: "#F2F2F2",
	selectedGround: "#F2F2F2",

	/* Borders */
	border: "#E4E4E4",
	borderStrong: "#CECECE",

	/* Text */
	text: "#1F1F1F",
	textSecondary: "#6C6C6D",
	textDisabled: "#9B9B9B",
	textOnPrimary: "#1F1F1F",
	textOnSecondary: "#FFFFFF",

	/* The one white-on-lime pairing in the system — the glyph of a SELECTED tool
	   button, where the lime fill itself is the state cue. Icons only; a lime
	   button's LABEL is still `textOnPrimary`. See DESIGN_SYSTEM.md §8. */
	iconOnPrimary: "#FFFFFF",

	/* Status */
	successBg: "#C5DF7A",
	infoBg: "#FBF4FF",
	infoText: "#7C4789",
	warningBg: "#FDECC8",
	neutralBg: "#E4E4E4",
	danger: "#B3261E",
	scrim: "rgba(31, 31, 31, 0.40)",
} as const;

export const font = {
	family: 'Roboto, "Helvetica Neue", Arial, sans-serif',

	sizeMicro: "10px",
	sizeCaption: "12px",
	sizeBody: "14px",
	sizeTitle: "16px",
	sizeHeading: "20px",

	/* Shifted one step lighter on 2026-08-22: bold 700 -> 500, medium 500 -> 400,
	   regular 400 -> 300. The names are ROLES, not weights — `weightBold` is still
	   "the emphasis weight". `weightLight` and `weightRegular` now coincide: the
	   scale has bottomed out at the lightest Roboto weight the app loads. See
	   DESIGN_SYSTEM.md §2. */
	weightLight: "300",
	weightRegular: "300",
	weightMedium: "400",
	weightBold: "500",

	lineHeightTight: "1.33",
	lineHeightBase: "1.4",
} as const;

export const space = {
	1: "4px",
	2: "8px",
	3: "12px",
	4: "16px",
	5: "20px",
	6: "24px",
	8: "32px",
	segmentGap: "6px",
} as const;

export const radius = {
	sm: "3px",
	md: "6px",
	lg: "12px",
	full: "9999px",
} as const;

export const border = {
	width: "1px",
	default: `1px solid ${colors.border}`,
	strong: `1px solid ${colors.borderStrong}`,
	active: `1px solid ${colors.secondary}`,
} as const;

export const shadow = {
	modal: "0 2px 12px rgba(0, 0, 0, 0.10)",
} as const;

export const size = {
	control: "48px",
	controlCompact: "40px",
	controlChip: "44px",
	/* Segmented control — its own size, not controlCompact. See tokens.css. */
	segmented: "28px",
	checkbox: "20px",
	toggleW: "38px",
	toggleH: "20px",
	toggleKnob: "16px",
	icon: "24px",
	iconStroke: "1.8",
	pill: "28px",
	avatar: "40px",

	/* Icon button — deliberately its own size, not controlCompact/icon. See
	   tokens.css. */
	iconButton: "32px",
	iconButtonIcon: "19px",

	topbar: "68px",
	sidebarLeft: "300px",
	sidebarRight: "340px",
	modalWidth: "776px",
	modalWidthCompact: "480px",
	pagePadding: "12px",
	gutter: "8px",
} as const;

export const motion = {
	transitionBase: "120ms ease-out",
	focusBorder: colors.primary,
	focusRing: `2px solid ${colors.secondary}`,
	focusRingOffset: "2px",
} as const;

/**
 * Every token, keyed by its CSS custom-property name. This is what the parity
 * test compares against tokens.css, so a new token must be added here too.
 */
export const cssVarMap: Record<string, string> = {
	"--color-primary": colors.primary,
	"--color-primary-tint": colors.primaryTint,
	"--color-primary-contrast": colors.primaryContrast,
	"--color-primary-fill": colors.primaryFill,
	"--color-primary-fill-hover": colors.primaryFillHover,
	"--color-primary-fill-active": colors.primaryFillActive,

	"--color-secondary": colors.secondary,
	"--color-secondary-tint": colors.secondaryTint,
	"--color-secondary-contrast": colors.secondaryContrast,
	"--color-secondary-fill": colors.secondaryFill,
	"--color-secondary-fill-hover": colors.secondaryFillHover,
	"--color-secondary-fill-active": colors.secondaryFillActive,

	"--logo-lime": colors.logoLime,
	"--logo-purple": colors.logoPurple,
	"--logo-navy": colors.logoNavy,
	"--logo-grey": colors.logoGrey,

	"--color-grey-50": colors.grey50,
	"--color-grey-100": colors.grey100,
	"--color-grey-200": colors.grey200,
	"--color-grey-300": colors.grey300,
	"--color-grey-400": colors.grey400,
	"--color-grey-500": colors.grey500,

	"--color-bg-app": colors.bgApp,
	"--color-bg-panel": colors.bgPanel,
	"--color-surface": colors.surface,
	"--color-fill-subtle": colors.fillSubtle,
	"--color-bg-workspace": colors.bgWorkspace,
	"--color-bg-viewport": colors.bgViewport,

	"--color-hover-ground": colors.hoverGround,
	"--color-selected-ground": colors.selectedGround,

	"--color-border": colors.border,
	"--color-border-strong": colors.borderStrong,

	"--color-text": colors.text,
	"--color-text-secondary": colors.textSecondary,
	"--color-text-disabled": colors.textDisabled,
	"--color-text-on-primary": colors.textOnPrimary,
	"--color-text-on-secondary": colors.textOnSecondary,
	"--color-icon-on-primary": colors.iconOnPrimary,

	"--color-success-bg": colors.successBg,
	"--color-info-bg": colors.infoBg,
	"--color-info-text": colors.infoText,
	"--color-warning-bg": colors.warningBg,
	"--color-neutral-bg": colors.neutralBg,
	"--color-danger": colors.danger,
	"--color-scrim": colors.scrim,

	"--font-family": font.family,
	"--font-size-micro": font.sizeMicro,
	"--font-size-caption": font.sizeCaption,
	"--font-size-body": font.sizeBody,
	"--font-size-title": font.sizeTitle,
	"--font-size-heading": font.sizeHeading,
	"--font-weight-light": font.weightLight,
	"--font-weight-regular": font.weightRegular,
	"--font-weight-medium": font.weightMedium,
	"--font-weight-bold": font.weightBold,
	"--line-height-tight": font.lineHeightTight,
	"--line-height-base": font.lineHeightBase,

	"--space-1": space[1],
	"--space-2": space[2],
	"--space-3": space[3],
	"--space-4": space[4],
	"--space-5": space[5],
	"--space-6": space[6],
	"--space-8": space[8],
	"--space-segment-gap": space.segmentGap,

	"--radius-sm": radius.sm,
	"--radius-md": radius.md,
	"--radius-lg": radius.lg,
	"--radius-full": radius.full,

	"--border-width": border.width,
	"--border-default": border.default,
	"--border-strong": border.strong,
	"--border-active": border.active,

	"--shadow-modal": shadow.modal,

	"--size-control": size.control,
	"--size-control-compact": size.controlCompact,
	"--size-control-chip": size.controlChip,
	"--size-segmented": size.segmented,
	"--size-checkbox": size.checkbox,
	"--size-toggle-w": size.toggleW,
	"--size-toggle-h": size.toggleH,
	"--size-toggle-knob": size.toggleKnob,
	"--size-icon": size.icon,
	"--size-icon-stroke": size.iconStroke,
	"--size-pill": size.pill,
	"--size-avatar": size.avatar,
	"--size-iconbutton": size.iconButton,
	"--size-iconbutton-icon": size.iconButtonIcon,
	"--size-topbar": size.topbar,
	"--size-sidebar-left": size.sidebarLeft,
	"--size-sidebar-right": size.sidebarRight,
	"--size-modal-width": size.modalWidth,
	"--size-modal-width-compact": size.modalWidthCompact,
	"--page-padding": size.pagePadding,
	"--gutter": size.gutter,

	"--transition-base": motion.transitionBase,
	"--focus-border": motion.focusBorder,
	"--focus-ring": motion.focusRing,
	"--focus-ring-offset": motion.focusRingOffset,
};

const tokens = { colors, font, space, radius, border, shadow, size, motion };

export default tokens;
